/**
 * Minimal i18n seam for the data-view package.
 *
 * The package itself ships English fallback strings and no i18n
 * runtime. The host app registers a translator at module-load time
 * (same pattern as `registerGlobalGroupByConfig`) that resolves keys
 * against its own i18n system — StreamLain registers one backed by
 * `@affine/i18n` under the `com.streamlain.dataview.*` namespace.
 *
 * Fallback templates use single-brace `{param}` placeholders which
 * `dataViewI18nT` interpolates itself; registered translators receive
 * the raw params and interpolate with their own engine.
 */

const dataViewI18nDefaults = {
  'canvas.loading.description': 'Local database data is being prepared.',
  'canvas.loading.title': 'Data is loading',
  'canvas.summary': 'Overview',
  'canvas.unavailable.title': 'Canvas view unavailable',
  'database.shardRepair.action': 'Repair shard metadata',
  'database.shardRepair.consequence':
    'Repair aligns the manifest row count and first/last row with the stored row order. Stored row data stays unchanged; absent rows are not restored.',
  'database.shardRepair.failed': 'Database metadata could not be repaired.',
  'database.shardRepair.success': 'Database metadata repaired.',
  'database.shardRepair.title': 'Database shard metadata is inconsistent',
  'calendar.accessibility.addFeed': 'Add read-only ICS feed',
  'calendar.accessibility.cancelOccurrence': 'Cancel this recurring occurrence',
  'calendar.accessibility.closeDetails': 'Close event details',
  'calendar.accessibility.copyFeed': 'Copy signed database calendar feed URL',
  'calendar.accessibility.createEvent': 'Create calendar event',
  'calendar.accessibility.createEventAt': 'Create event on {date} at {time}',
  'calendar.accessibility.createEventOn': 'Create event on {date}',
  'calendar.accessibility.deleteEvent': 'Delete calendar event',
  'calendar.accessibility.editOccurrence': 'Edit this recurring occurrence',
  'calendar.accessibility.expandAllDay': 'Show all-day events',
  'calendar.accessibility.collapseAllDay': 'Hide all-day events',
  'calendar.accessibility.exportEvent': 'Export event as ICS',
  'calendar.accessibility.exportVisible':
    'Export visible calendar events as ICS',
  'calendar.accessibility.goToday': 'Go to today',
  'calendar.accessibility.importIcs': 'Import calendar events from ICS',
  'calendar.accessibility.jumpToEarlierEvents':
    'Show {count} earlier hidden events',
  'calendar.accessibility.jumpToLaterEvents':
    'Show {count} later hidden events',
  'calendar.accessibility.moreActions': 'More calendar actions',
  'calendar.accessibility.nextRange': 'Next calendar range',
  'calendar.accessibility.openDay': 'Open {date} in day calendar',
  'calendar.accessibility.openMoreEvents': 'Open {count} more events on {date}',
  'calendar.accessibility.previousRange': 'Previous calendar range',
  'calendar.accessibility.recurrenceCount': 'Recurrence count',
  'calendar.accessibility.recurrenceInterval': 'Recurrence interval',
  'calendar.accessibility.recurrenceUntil': 'Recurrence until date',
  'calendar.accessibility.removeAttendee': 'Remove attendee {name}',
  'calendar.accessibility.removeException': 'Remove exception {date}',
  'calendar.accessibility.removeFeed': 'Remove ICS feed {name}',
  'calendar.accessibility.refreshFeed': 'Refresh ICS feed {name}',
  'calendar.accessibility.resizeDetails': 'Resize event details panel',
  'calendar.accessibility.resizeEnd': 'Resize event end',
  'calendar.accessibility.resizeStart': 'Resize event start',
  'calendar.accessibility.sendUpdates':
    'Send calendar invitations, updates and reminders',
  'calendar.accessibility.switchMode': 'Switch to {mode} calendar',
  'calendar.accessibility.toggleCalendar': 'Toggle calendar {name}',
  'calendar.accessibility.toggleFeed': 'Toggle ICS feed {name}',
  'calendar.action.add': 'Add',
  'calendar.action.addFeed': 'Add feed',
  'calendar.action.apply': 'Apply',
  'calendar.action.cancel': 'Cancel',
  'calendar.action.cancelOccurrence': 'Cancel occurrence',
  'calendar.action.completeFields': 'Complete',
  'calendar.action.copy': 'Copy',
  'calendar.action.copyFeed': 'Copy feed',
  'calendar.action.cut': 'Cut',
  'calendar.action.delete': 'Delete',
  'calendar.action.editOccurrence': 'Edit occurrence',
  'calendar.action.export': 'Export',
  'calendar.action.exportIcs': 'Export ICS',
  'calendar.action.getStarted': "Let's go",
  'calendar.action.import': 'Import',
  'calendar.action.more': 'More',
  'calendar.action.new': 'New',
  'calendar.action.notNow': 'Not now',
  'calendar.action.paste': 'Paste',
  'calendar.action.sendCancellation': 'Send cancellation',
  'calendar.action.sendUpdates': 'Send updates',
  'calendar.action.sending': 'Sending...',
  'calendar.action.today': 'Today',
  'calendar.confirm.sendCancellation':
    'Send a cancellation to invited attendees?',
  'calendar.confirm.suppressMailPrompt':
    'Do not ask again for this calendar for 30 minutes?',
  'calendar.confirm.sendUpdate': 'Send an update to invited attendees?',
  'calendar.detail.address': 'Address',
  'calendar.detail.allDay': 'All day',
  'calendar.detail.attendeeConflict': '{count} attendee conflict',
  'calendar.detail.attendeeConflicts': '{count} attendee conflicts',
  'calendar.detail.attendees': 'Attendees',
  'calendar.detail.calendar': 'Calendar',
  'calendar.detail.confirmed': 'Confirmed',
  'calendar.detail.confidential': 'Confidential',
  'calendar.detail.count': 'Count',
  'calendar.detail.daysBefore': 'days before',
  'calendar.detail.default': 'Default',
  'calendar.detail.description': 'Description',
  'calendar.detail.doesNotRepeat': 'Does not repeat',
  'calendar.detail.end': 'End',
  'calendar.detail.event': 'Event',
  'calendar.detail.exceptions': 'Exceptions',
  'calendar.detail.hoursBefore': 'hours before',
  'calendar.detail.minutesBefore': 'minutes before',
  'calendar.detail.nameEmailPlaceholder': 'Name <email@example.com>',
  'calendar.detail.noAttendees': 'No attendees',
  'calendar.detail.noSkippedDates': 'No skipped dates',
  'calendar.detail.private': 'Private',
  'calendar.detail.public': 'Public',
  'calendar.detail.readonlyFeedEvent': 'Read-only ICS feed event',
  'calendar.detail.recurrence': 'Recurrence',
  'calendar.detail.recurrenceDaily': 'Daily',
  'calendar.detail.recurrenceEveryLabel': 'Every',
  'calendar.detail.recurrenceFrequencyLabel': 'Frequency',
  'calendar.detail.recurrenceId': 'Recurrence ID',
  'calendar.detail.recurrenceMonthly': 'Monthly',
  'calendar.detail.recurrenceOccurrence': 'Recurring occurrence: {date}',
  'calendar.detail.recurrenceUntilLabel': 'Until',
  'calendar.detail.recurrenceWeekly': 'Weekly',
  'calendar.detail.recurrenceYearly': 'Yearly',
  'calendar.detail.reminder': 'Reminder',
  'calendar.detail.start': 'Start',
  'calendar.detail.status': 'Status',
  'calendar.detail.statusCancelled': 'Cancelled',
  'calendar.detail.statusTentative': 'Tentative',
  'calendar.detail.syncFailed': 'Calendar updates could not be sent.',
  'calendar.detail.syncSent': 'Calendar updates sent.',
  'calendar.detail.time': 'Time',
  'calendar.detail.timezone': 'Timezone',
  'calendar.detail.title': 'Title',
  'calendar.detail.virtualLink': 'Virtual link',
  'calendar.detail.visibility': 'Visibility',
  'calendar.empty.deletedDescription':
    'A required date column was removed. Reconnect it so your events can be displayed.',
  'calendar.empty.deletedTitle': 'Reconnect calendar',
  'calendar.empty.description':
    'We create the matching columns automatically and show database rows as calendar events.',
  'calendar.empty.setupCta': 'Set up calendar',
  'calendar.empty.title': 'Set up calendar',
  'calendar.event.importedTitle': 'Imported event',
  'calendar.event.invitationPreview': 'Invitation',
  'calendar.event.newTitle': 'New event',
  'calendar.event.untitled': 'Untitled event',
  'calendar.error.deleteReconcile':
    'Calendar delete side effects could not be reconciled',
  'calendar.error.exportFailed': 'Calendar export failed',
  'calendar.error.feedFailed': 'Calendar feed failed',
  'calendar.error.feedInvalid': 'Feed URL is invalid',
  'calendar.error.feedLoadFailed':
    'Calendar feed "{name}" could not be loaded: {message}',
  'calendar.error.feedUrlCreateFailed':
    'Calendar feed URL could not be created',
  'calendar.error.recurrenceInvalid': 'Invalid calendar recurrence rule',
  'calendar.error.mailAccountRequired':
    'Connect a mail account before sending calendar invitations, updates or cancellations.',
  'calendar.error.mailAccountsFailed':
    'Calendar mail accounts could not be loaded',
  'calendar.error.mailboxCredentialRequired':
    'Mailbox credential access is required before sending calendar updates.',
  'calendar.error.mailboxUnavailable':
    'Linked mailbox cannot send calendar updates.',
  'calendar.error.recurrenceExceptionMissing':
    'Calendar recurrence exception field is missing.',
  'calendar.error.recurrenceFreqInvalid':
    'Frequency must be daily, weekly, monthly or yearly.',
  'calendar.error.recurrenceIdMissing':
    'Calendar recurrence ID field is missing.',
  'calendar.error.recurrenceIntervalInvalid':
    'Interval must be a positive number.',
  'calendar.error.recurrenceCountInvalid': 'Count must be a positive number.',
  'calendar.error.recurrenceMonthDayInvalid':
    'Month days must be between -31 and 31 and cannot be zero.',
  'calendar.error.recurrenceRuleInvalid': 'Invalid recurrence rule',
  'calendar.error.importFailed': 'Calendar import failed',
  'calendar.error.updatesFailed': 'Calendar updates failed',
  'calendar.grid.allDay': 'All day',
  'calendar.grid.hiddenEarlier': '{count} earlier hidden',
  'calendar.grid.hiddenLater': '{count} later hidden',
  'calendar.grid.invalidRecurrence': 'Invalid recurrence',
  'calendar.grid.loading': 'Loading',
  'calendar.grid.moreEvents': '+{count} more',
  'calendar.mail.account': 'Mail account',
  'calendar.mailboxLink.notLinked': 'No linked mailbox',
  'calendar.mailboxLink.outboundLinked': 'Linked mailbox',
  'calendar.mailboxLink.title': 'Mailbox link',
  'calendar.mailboxLink.unavailable': 'Linked mailbox unavailable',
  'calendar.mail.scope.private': 'Private',
  'calendar.mail.scope.shared': 'Shared',
  'calendar.mode.day': 'Day',
  'calendar.mode.month': 'Month',
  'calendar.mode.week': 'Week',
  'calendar.notice.missingFields': 'Calendar fields missing: {fields}',
  'calendar.notice.partialProjection': 'Wird aktualisiert...',
  'calendar.notice.projectionUnsupported':
    'Ansicht muss neu eingerichtet werden',
  'calendar.notice.savedWithoutMail': 'Saved, not sent',
  'calendar.notice.savedOffline': 'Saved locally, syncs when reconnected',
  'calendar.toolbar.feedUrl': 'ICS feed URL',
  'calendar.wizard.advancedCollapse': 'Collapse {title}',
  'calendar.wizard.advancedDescription':
    'Optional: reuse columns, adjust names or review details.',
  'calendar.wizard.advancedExpand': 'Expand {title}',
  'calendar.wizard.advancedTitle': 'Advanced settings',
  'calendar.wizard.automaticSummary':
    'Start, end, event details, recurrence, status and visibility are configured automatically.',
  'calendar.wizard.autoCreateLabel': 'New "{defaultName}" column',
  'calendar.wizard.chooseOrCreatePlaceholder': 'Choose or create column...',
  'calendar.wizard.createNewColumn': '+ Create new column',
  'calendar.wizard.dateExistingHint':
    'Choose existing date columns or create them directly.',
  'calendar.wizard.datesAutoHint': 'We create start and end columns for you.',
  'calendar.wizard.datesTitle': 'When do the events begin and end?',
  'calendar.wizard.description':
    'We configure everything automatically. Open advanced settings only if you want to reuse existing columns or adjust names.',
  'calendar.wizard.detailsAutoHint':
    'We detect existing detail columns and create missing columns automatically.',
  'calendar.wizard.detailsCollapsedHint':
    'Event details are configured automatically. Open this section only for adjustments.',
  'calendar.wizard.detailsDescription':
    'Calendar color, all-day marker, place, meeting link, attendees and notes.',
  'calendar.wizard.detailsTitle': 'Event details',
  'calendar.wizard.endLabel': 'End',
  'calendar.wizard.foundBadge': 'Found',
  'calendar.wizard.matchedSuffix': '  matched',
  'calendar.wizard.missingColumnNote':
    'We create the missing {missing} column for you.',
  'calendar.wizard.newColumnNamePlaceholder': 'Column name',
  'calendar.wizard.oneDateHint':
    'You have one date column: {column}. What should it represent?',
  'calendar.wizard.recurrenceCollapsedHint':
    'Recurrence and reminders are configured automatically.',
  'calendar.wizard.recurrenceDescription':
    'Recurring events, exceptions, timezone and reminders.',
  'calendar.wizard.recurrenceTitle': 'Recurrence and reminders',
  'calendar.wizard.role.allDay.description': 'Marks all-day events.',
  'calendar.wizard.role.allDay.label': 'All day',
  'calendar.wizard.role.allDay.name': 'All day',
  'calendar.wizard.role.attendees.description':
    'People and resources for invitations.',
  'calendar.wizard.role.attendees.label': 'Attendees',
  'calendar.wizard.role.attendees.name': 'Attendees',
  'calendar.wizard.role.calendarId.description': 'Calendar, color or resource.',
  'calendar.wizard.role.calendarId.label': 'Calendar',
  'calendar.wizard.role.calendarId.name': 'Calendar',
  'calendar.wizard.role.description.description': 'Notes and agenda.',
  'calendar.wizard.role.description.label': 'Description',
  'calendar.wizard.role.description.name': 'Description',
  'calendar.wizard.role.end.description': 'Event end time.',
  'calendar.wizard.role.end.label': 'End',
  'calendar.wizard.role.end.name': 'End',
  'calendar.wizard.role.locationPhysical.description': 'Room or address.',
  'calendar.wizard.role.locationPhysical.label': 'Place',
  'calendar.wizard.role.locationPhysical.name': 'Place',
  'calendar.wizard.role.locationVirtual.description': 'Video or meeting link.',
  'calendar.wizard.role.locationVirtual.label': 'Meeting link',
  'calendar.wizard.role.locationVirtual.name': 'Meeting link',
  'calendar.wizard.role.recurrence.description': 'Recurrence rule.',
  'calendar.wizard.role.recurrence.label': 'Recurrence',
  'calendar.wizard.role.recurrence.name': 'Recurrence',
  'calendar.wizard.role.recurrenceExdate.description': 'Exceptions for series.',
  'calendar.wizard.role.recurrenceExdate.label': 'Exceptions',
  'calendar.wizard.role.recurrenceExdate.name': 'Exceptions',
  'calendar.wizard.role.recurrenceId.description': 'Detached series instances.',
  'calendar.wizard.role.recurrenceId.label': 'Series ID',
  'calendar.wizard.role.recurrenceId.name': 'Series ID',
  'calendar.wizard.role.reminder.description': 'Reminder before start.',
  'calendar.wizard.role.reminder.label': 'Reminder',
  'calendar.wizard.role.reminder.name': 'Reminder',
  'calendar.wizard.role.start.description': 'Event start time.',
  'calendar.wizard.role.start.label': 'Start',
  'calendar.wizard.role.start.name': 'Start',
  'calendar.wizard.role.status.description':
    'Confirmed, tentative or cancelled.',
  'calendar.wizard.role.status.label': 'Status',
  'calendar.wizard.role.status.name': 'Status',
  'calendar.wizard.role.timezone.description': 'Timezone for series.',
  'calendar.wizard.role.timezone.label': 'Timezone',
  'calendar.wizard.role.timezone.name': 'Timezone',
  'calendar.wizard.role.visibility.description': 'Public or private.',
  'calendar.wizard.role.visibility.label': 'Visibility',
  'calendar.wizard.role.visibility.name': 'Visibility',
  'calendar.wizard.roleEnd': 'End date',
  'calendar.wizard.roleStart': 'Start date',
  'calendar.wizard.startLabel': 'Start',
  'calendar.wizard.statusCollapsedHint':
    'Status and visibility are configured automatically.',
  'calendar.wizard.statusDescription':
    'Confirmed, tentative, cancelled, plus public and private events.',
  'calendar.wizard.statusTitle': 'Status and visibility',
  'calendar.wizard.title': 'Plan your events in the calendar',
  'gantt.accessibility.goToday': 'Go to today',
  'gantt.accessibility.nextRange': 'Next gantt range',
  'gantt.accessibility.previousRange': 'Previous gantt range',
  'gantt.accessibility.switchZoom': 'Switch to {zoom} gantt zoom',
  'gantt.action.today': 'Today',
  'gantt.empty.configureColumns': 'Configure columns',
  'gantt.empty.description':
    'Pick a start and end date column to see your tasks on the gantt.',
  'gantt.empty.title': 'Date columns not configured',
  'gantt.label.collapseTitleColumn': 'Collapse title column',
  'gantt.label.showFullLabelColumns': 'Show full label columns',
  'gantt.label.showHideProperties': 'Show/hide properties',
  'gantt.label.showTitleOnly': 'Show title only',
  'gantt.bar.assignee': 'Assignee',
  'gantt.bar.end': 'End',
  'gantt.bar.progress': 'Progress',
  'gantt.bar.start': 'Start',
  'gantt.bar.status': 'Status',
  'gantt.bar.title': 'Title',
  'gantt.bar.untitled': 'Untitled',
  'gantt.barDisplay.assigneeMode': 'Assignee mode',
  'gantt.barDisplay.description':
    'Drag to reorder and toggle visibility on bars.',
  'gantt.barDisplay.hideOnBars': 'Hide on bars',
  'gantt.barDisplay.reset': 'Reset',
  'gantt.barDisplay.resetToDefaults': 'Reset to defaults',
  'gantt.barDisplay.showOnBars': 'Show on bars',
  'gantt.barDisplay.title': 'Bar display',
  'gantt.barDisplay.titleAlwaysVisible': 'Title is always visible',
  'gantt.barDisplay.variantAvatar': 'Avatar',
  'gantt.barDisplay.variantBoth': 'Both',
  'gantt.barDisplay.variantName': 'Name',
  'gantt.missingColumns.assignee': 'Assignee',
  'gantt.missingColumns.dependencies': 'Dependencies',
  'gantt.missingColumns.end': 'End date',
  'gantt.missingColumns.multiple':
    '{{count}} columns were deleted ({{labels}}) - reconfigure the gantt.',
  'gantt.missingColumns.progress': 'Progress',
  'gantt.missingColumns.single':
    'The {{label}} column was deleted - reconfigure the gantt to restore its bars.',
  'gantt.missingColumns.start': 'Start date',
  'gantt.missingColumns.status': 'Status',
  'gantt.notice.partialProjection': 'Wird aktualisiert...',
  'gantt.settings.barDisplay': 'Bar display settings',
  'gantt.settings.group': 'Group',
  'gantt.settings.snap': 'Snap',
  'gantt.snap.day': 'Day',
  'gantt.snap.none': 'None',
  'gantt.validation.startAfterEnd': 'Start date is after end date',
  'gantt.snap.week': 'Week',
  'gantt.zoom.day': 'Day',
  'gantt.zoom.month': 'Month',
  'gantt.zoom.quarter': 'Quarter',
  'gantt.zoom.week': 'Week',
  'dateCell.dateTimeHint': 'Choose a date in the calendar and adjust the time.',
  'dateCell.dateTimeTitle': 'Date and time',
  'dateCell.selectedDate': 'Date',
  'dateCell.selectedTime': 'Time',
  'dateCell.quickTimes': 'Quick times',
  'dateCell.quickTimeEvening': '17:00',
  'dateCell.quickTimeMorning': '09:00',
  'dateCell.quickTimeNoon': '12:00',
  'dateCell.quickTimeNow': 'Now',
  'dateCell.time': 'Time',
  'row.expand': 'Expand Row',
  'table.defaultName': 'Table View',
  'toolbar.importFiles': 'Import Files',
  'toolbar.newRecord': 'New Record',
  'toolbar.newRecordShort': 'New',
  'toolbar.searchClear': 'Clear search',
  'toolbar.searchPlaceholder': 'Search...',
  'view.add': 'Add View',
  'view.create': 'Create {name}',
  'view.delete': 'Delete',
  'view.duplicate': 'Duplicate',
  'view.edit': 'Edit View',
  'view.moveLeft': 'Move Left',
  'view.moveRight': 'Move Right',
  'view.namePlaceholder': 'View name',
  'view.switch': 'Switch View',
  'viewSettings.cellDisplay': 'Cell Display',
  'viewSettings.cellDisplay.collapsed': 'Collapsed',
  'viewSettings.cellDisplay.expanded': 'Expanded',
  'viewSettings.cellDisplay.smart': 'Smart Expand',
  'viewSettings.filter': 'Filter',
  'viewSettings.filterCount': '{count} filters',
  'viewSettings.filterCountOne': '1 filter',
  'viewSettings.group': 'Group',
  'viewSettings.layout': 'Layout',
  'viewSettings.layout.gantt': 'Gantt',
  'viewSettings.layout.kanban': 'Kanban',
  'viewSettings.layout.table': 'Table',
  'viewSettings.properties': 'Properties',
  'viewSettings.propertiesShown': '{count} shown',
  'viewSettings.sort': 'Sort',
  'viewSettings.sortCount': '{count} sorts',
  'viewSettings.sortCountOne': '1 sort',
  'viewSettings.subGroup': 'Sub group',
  'viewSettings.title': 'View settings',
  'kanban.showMoreColumns': '+{count} columns',
  'kanban.showMoreColumnsTitle': 'Show more columns',
  'kanban.showMoreRowGroups': '+{count} row groups',
  'kanban.showMoreRowGroupsTitle': 'Show more row groups',
  'kanban.showMoreCards': '+{count} more',
  'kanban.showMoreCardsTitle': 'Show more cards',
  'kanban.addGroup': 'Add group',
  'kanban.addSubgroup': 'Add subgroup',
  'kanban.newGroupTitle': 'New group · {property}',
  'kanban.newGroupTitleWithType': 'New group · {property} ({type})',
  'kanban.groupNamePlaceholder': 'Group name',
  'kanban.reorderColumnGroup': 'Drag to reorder this column group',
  'kanban.reorderRowGroup': 'Drag to reorder this row group',
  'kanban.moveRowGroupUp': 'Move row group up',
  'kanban.moveRowGroupDown': 'Move row group down',
  'kanban.reorderRequiresDefaultSort':
    'Set group sorting to Default to reorder manually',
  'property.delete': 'Delete',
  'property.deleteConfirm':
    'Delete column "{name}"? All data in this column will be permanently lost.',
  'property.deleteFallbackName': 'this column',
  'property.duplicate': 'Duplicate',
  'property.filter': 'Filter',
  'property.hideInView': 'Hide In View',
  'property.insertLeft': 'Insert Left Column',
  'property.insertRight': 'Insert Right Column',
  'property.moveLeft': 'Move Left',
  'property.moveRight': 'Move Right',
  'property.namePlaceholder': 'Property name',
  'property.numberFormat': 'Number Format',
  'property.settings': 'Property settings',
  'property.sortAscending': 'Sort Ascending',
  'property.sortDescending': 'Sort Descending',
  'property.type': 'Type',
  'property.type.attachment': 'Attachment',
  'property.type.checkbox': 'Checkbox',
  'property.type.created-by': 'Created By',
  'property.type.date': 'Date',
  'property.type.doc-reference': 'Doc Reference',
  'property.type.image': 'Image',
  'property.type.member': 'Member',
  'property.type.multi-select': 'Multi-select',
  'property.type.number': 'Number',
  'property.type.progress': 'Progress',
  'property.type.relation': 'Relation',
  'property.type.rollup': 'Rollup',
  'property.type.select': 'Select',
  'property.type.text': 'Plain-Text',
  'property.type.title': 'Title',
  'property.type.updated-by': 'Updated By',
  'property.typeTitle': 'Property type',
} as const;

export type DataViewI18nKey = keyof typeof dataViewI18nDefaults;

export type DataViewI18nParams = Record<string, string | number>;

/**
 * Resolves a data-view i18n key to a fully interpolated string, or
 * `undefined` when the host has no translation — the package fallback
 * is used then.
 */
export type DataViewTranslator = (
  key: DataViewI18nKey,
  params: DataViewI18nParams | undefined
) => string | undefined;

let globalDataViewTranslator: DataViewTranslator | undefined;
let globalDataViewLocaleProvider: (() => string | undefined) | undefined;

export const registerDataViewTranslator = (
  translator: DataViewTranslator
): void => {
  globalDataViewTranslator = translator;
};

export const registerDataViewLocaleProvider = (
  provider: () => string | undefined
): void => {
  globalDataViewLocaleProvider = provider;
};

export const getDataViewRuntimeLocale = (): string | undefined => {
  const registered = globalDataViewLocaleProvider?.();
  if (registered) {
    return registered;
  }
  if (typeof document !== 'undefined') {
    const documentLanguage = document.documentElement.lang;
    if (documentLanguage) {
      return documentLanguage;
    }
  }
  if (typeof navigator !== 'undefined') {
    return navigator.language;
  }
  return undefined;
};

const interpolate = (
  template: string,
  params: DataViewI18nParams | undefined
): string => {
  if (!params) {
    return template;
  }
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in params ? String(params[name]) : match
  );
};

export const dataViewI18nT = (
  key: DataViewI18nKey,
  params?: DataViewI18nParams
): string => {
  const translated = globalDataViewTranslator?.(key, params);
  if (translated) {
    return translated;
  }
  return interpolate(dataViewI18nDefaults[key], params);
};
